/**
 * ZavadyModal — the damage map of one fleet car (per-row modal): the exterior drawing coloured by severity, the
 * list of current faults, and a form for the selected part. `pujcovna:write` sets faults and marks them repaired (a
 * repaired fault leaves the map but stays on the car, in the "Opravené závady" list, and can be reopened), one part per
 * save (the API's unit); only `pujcovna:admin` deletes a fault entered by mistake; `read` sees the same map and lists. Two views of the same fault list (plan 3.16): the
 * legacy exterior drawing and the interior as a grouped picker.
 */

import { Button } from '@/components/Button';
import { Modal, DialogCrown } from '@/components/Modal';
import { useConfirm } from '@/components/ConfirmDialog';
import { Skeleton } from '@/components/Skeleton';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import {
  useClearZavadaMutation,
  useDilyQuery,
  useOpraveneZavadyQuery,
  useReopenZavadaMutation,
  useRepairZavadaMutation,
  useSetZavadaMutation,
  useZavadyQuery,
} from './api';
import { OpraveneZavady } from './OpraveneZavady';
import { ZavadyEditor } from './ZavadyEditor';

interface ZavadyModalProps {
  /** The fleet entry id. */
  id: number;
  /** What the dialog titles the car with (plate or VIN). */
  title: string;
  canWrite: boolean;
  /** `pujcovna:admin`: may delete a fault entered by mistake. */
  canAdmin?: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ZavadyModal({ id, title, canWrite, canAdmin = false, open, onOpenChange }: ZavadyModalProps) {
  const { confirm, dialog } = useConfirm();
  const query = useZavadyQuery(id);
  const set = useSetZavadaMutation(id);
  const clear = useClearZavadaMutation(id);
  const repair = useRepairZavadaMutation(id);
  const reopen = useReopenZavadaMutation(id);
  const opraveneQuery = useOpraveneZavadyQuery(id);
  const dily = useDilyQuery();
  const busy = set.isPending || clear.isPending || repair.isPending || reopen.isPending;
  // Every way out (Zavřít, ×, Esc, a click outside, the sheet's drag) ends in `onOpenChange(false)`. While a write
  // is pending that would unmount the observer its onSuccess / onError toasts hang on, so a failed save would
  // vanish without a word: the modal stays until the request has settled.
  const guarded = (next: boolean) => {
    if (!next && busy) return;
    onOpenChange(next);
  };

  // The modal mounts per open: the map renders only from a read that finished after that mount, never from a
  // cached snapshot another member may have changed, and a failed read blocks it (the same rule as Pojištění).
  const fresh = query.isSuccess && query.isFetchedAfterMount;
  const faults = query.data;
  const parts = dily.data;

  return (
    <>
    <Modal open={open} onOpenChange={guarded} maxWidth={960}>
      <DialogCrown
        eyebrow="Vozidlo půjčovny"
        title={
          <>
            Závady <em>{title}</em>
          </>
        }
        sub="Vyberte díl na mapě vnějšku, nebo v seznamu interiéru. Barva dílu ukazuje závažnost jeho aktuální závady."
      />
      <Modal.Body className="px-8 py-5">
        {query.isError || dily.isError ? (
          <p data-testid="zavady-error" className="text-[13px] text-ink-2">
            Závady se nepodařilo načíst. Zkuste to prosím znovu za chvíli.
          </p>
        ) : !fresh || !faults || !parts ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            <ZavadyEditor
              faults={faults}
              parts={parts}
              canWrite={canWrite}
              busy={busy}
              onSave={(dil, body) =>
                set.mutate(
                  { dil, body },
                  {
                    onSuccess: () => toast.ok('Závada uložena.'),
                    onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se uložit závadu.'}`),
                  },
                )
              }
              onRepair={(dil, poznamka) =>
                repair.mutate(
                  { dil, poznamka },
                  {
                    onSuccess: () => toast.ok('Závada označena jako opravená.'),
                    onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se označit závadu jako opravenou.'}`),
                  },
                )
              }
              onDelete={
                canAdmin
                  ? (fault) =>
                      void confirm({
                        title: 'Smazat závadu?',
                        description: `Závada „${fault.popis}“ se smaže bez záznamu o opravě — použijte jen pro závadu zadanou omylem. Opravenou závadu označte tlačítkem Opraveno.`,
                        confirmLabel: 'Smazat',
                        tone: 'danger',
                      }).then((ok) => {
                        if (!ok) return;
                        clear.mutate(fault.id_dilu, {
                          onSuccess: () => toast.ok('Závada smazána.'),
                          onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se smazat závadu.'}`),
                        });
                      })
                  : undefined
              }
            />
            {/* a secondary list: its failure must not take the active map (the main job of the modal) down with it */}
            {opraveneQuery.isError ? (
              <p data-testid="opravene-error" className="text-[13px] text-ink-2">
                Opravené závady se nepodařilo načíst. Zkuste to prosím znovu za chvíli.
              </p>
            ) : !opraveneQuery.isFetchedAfterMount || !opraveneQuery.data ? (
              <Skeleton className="h-16 w-full" />
            ) : (
              <OpraveneZavady
                opravene={opraveneQuery.data.opravene}
                celkem={opraveneQuery.data.celkem}
                active={faults}
                canWrite={canWrite}
                busy={busy}
                onReopen={(oid) =>
                  reopen.mutate(oid, {
                    onSuccess: () => toast.ok('Závada znovu otevřena.'),
                    onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se závadu znovu otevřít.'}`),
                  })
                }
              />
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Foot
        actions={
          <Button variant="secondary" onClick={() => guarded(false)} disabled={busy}>
            Zavřít
          </Button>
        }
      />
    </Modal>
    {dialog}
    </>
  );
}
